import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { showsApi } from '../api/shows'
import type { ShowCampaign, ShowCampaignDailyStatsResponse, ShowCampaignRequest, ShowCampaignStats } from '../types'

export const useShowCampaigns = () => {
  return useQuery<ShowCampaign[], Error>({
    queryKey: ['show-campaigns'],
    queryFn: showsApi.listCampaigns,
    staleTime: 60 * 1000,
  })
}

export const useShowCampaign = (id?: string) => {
  return useQuery<ShowCampaign, Error>({
    queryKey: ['show-campaign', id],
    queryFn: () => showsApi.getCampaign(id!),
    enabled: !!id,
    staleTime: 60 * 1000,
  })
}

export const useShowCampaignStats = (id?: string) => {
  return useQuery<ShowCampaignStats, Error>({
    queryKey: ['show-campaign-stats', id],
    queryFn: () => showsApi.getStats(id!),
    enabled: !!id,
    staleTime: 30 * 1000,
  })
}

export const useShowCampaignDailyStats = (id?: string, startDate?: string, endDate?: string) => {
  return useQuery<ShowCampaignDailyStatsResponse, Error>({
    queryKey: ['show-campaign-daily-stats', id, startDate, endDate],
    queryFn: () => showsApi.getDailyStats(id!, startDate, endDate),
    enabled: !!id,
    staleTime: 30 * 1000,
  })
}

export const useCreateShowCampaign = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: ShowCampaignRequest) => showsApi.createCampaign(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['show-campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['show-campaign-daily-stats'] })
    },
  })
}

export const useUpdateShowCampaign = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ShowCampaignRequest> }) => showsApi.updateCampaign(id, data),
    onSuccess: (campaign) => {
      queryClient.invalidateQueries({ queryKey: ['show-campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['show-campaign', campaign.id] })
      queryClient.invalidateQueries({ queryKey: ['show-campaign-stats', campaign.id] })
      queryClient.invalidateQueries({ queryKey: ['show-campaign-daily-stats', campaign.id] })
    },
  })
}

export const useDeleteShowCampaign = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => showsApi.deleteCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['show-campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['show-campaign'] })
      queryClient.invalidateQueries({ queryKey: ['show-campaign-stats'] })
      queryClient.invalidateQueries({ queryKey: ['show-campaign-daily-stats'] })
    },
  })
}
